import { getStockShortInterest, getStockShortsDailyInterestNew, getStockShortsTimeRanges } from "@/controllers/stock-shorts";
import { useState, useEffect, useRef } from "react";
import { useAtomValue } from "jotai";
import { userState } from "@/lib/store";
import { cardSeriesArgs, CardSeriesArgs, DEFAULT_SHORTS_TIME_RANGE, SHORTS_TIME_RANGE_SETTING } from "@/lib/short-interest-preload";

const SHORT_INTEREST_SETTINGS_KEY = SHORTS_TIME_RANGE_SETTING;

const useStockShortsCards = (stock) => {
    const user = useAtomValue(userState);
    const savedTimeRange: string | undefined = user?.settings?.module_settings?.[SHORT_INTEREST_SETTINGS_KEY]?.value;

    const [data, setData] = useState([]);
    const [keys, setKeys] = useState([]);
    const isMounted = useRef(false);
    // Start in the loading state so the first paint shows skeleton cards, not an empty row.
    const [loading, setLoading] = useState(true);
    const [obfuscated, setObfuscated] = useState(false);
    const [timeRanges, setTimeRanges] = useState([]);
    const [activeTimeRange, setActiveTimeRange] = useState<string>(savedTimeRange || DEFAULT_SHORTS_TIME_RANGE);
    const [shortInterest, setShortInterest] = useState({});

    useEffect(() => {
        if (savedTimeRange && savedTimeRange !== activeTimeRange) {
            setActiveTimeRange(savedTimeRange);
        }
    }, [savedTimeRange]);

    useEffect(() => {
        getStockShortsTimeRanges()
            .then((data) => {
                if (data.obfuscated) {
                    setObfuscated(true);
                    delete data.obfuscated;
                }
                return data;
            })
            .then((data) => setTimeRanges(data));
    }, []);

    // A different stock must not keep showing the previous stock's cards while its own load.
    useEffect(() => {
        isMounted.current = false;
        setShortInterest({});
        setData([]);
    }, [stock.tradingitem_id]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        // FIRST CALL
        getStockShortInterest(stock.tradingitem_id, activeTimeRange)
            .then((data) => {
                if (data.obfuscated) {
                    setObfuscated(true);
                    delete data.obfuscated;
                }
                return data;
            })
            .then((data) => {
                if (cancelled) return [] as CardSeriesArgs[];
                setShortInterest(data);

                // The same requests the overview may already have started (#1350): see cardSeriesArgs.
                const series = cardSeriesArgs(stock, data, activeTimeRange);
                setKeys(series.map(([, metric]) => metric));
                return series;
            })
            .then((series) => {
                return Promise.all(
                    series.map((args) => {
                        const key = args[1];

                        return getStockShortsDailyInterestNew(...args).then((res) => {
                            const filteredData = res.data?.filter(([date, value]) => {
                                return typeof value === "number";
                            });

                            // Each card's chart fills in as soon as its own series arrives.
                            if (!cancelled) setData((prev) => [...prev.filter((el) => Object.keys(el)[0] !== key), { [key]: filteredData }]);
                            return { [key]: filteredData };
                        });
                    })
                );
            })
            .finally(() => {
                if (cancelled) return;
                setLoading(false);
                isMounted.current = true;
            });
        return () => {
            cancelled = true;
        };
    }, [stock, activeTimeRange]);

    return {
        stock,
        data,
        keys,
        isMounted,
        loading,
        obfuscated,
        timeRanges,
        activeTimeRange,
        shortInterest,

        setActiveTimeRange,
    };
};

export default useStockShortsCards;
